import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, Video, Phone, ArrowRight, ArrowLeft, Check, Loader2, User, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function getMonthData(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return { firstDay, daysInMonth };
}

function formatDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export default function Booking() {
  const [step, setStep] = useState(1); // 1: date, 2: time+type, 3: details, 4: confirmed
  const [currentMonth, setCurrentMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [meetingType, setMeetingType] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { firstDay, daysInMonth } = getMonthData(currentMonth.year, currentMonth.month);

  const isWeekday = (day) => {
    const d = new Date(currentMonth.year, currentMonth.month, day);
    return d.getDay() >= 1 && d.getDay() <= 5;
  };

  const isPast = (day) => {
    const d = new Date(currentMonth.year, currentMonth.month, day);
    return d < today;
  };

  const selectDate = async (day) => {
    const d = new Date(currentMonth.year, currentMonth.month, day);
    setSelectedDate(d);
    setSelectedTime(null);
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API}/bookings/slots?date=${formatDate(d)}`);
      setSlots(res.data.slots);
    } catch (e) { toast.error("Failed to load slots"); }
    finally { setLoadingSlots(false); }
    setStep(2);
  };

  const prevMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 };
      return { ...prev, month: prev.month - 1 };
    });
  };

  const nextMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 };
      return { ...prev, month: prev.month + 1 };
    });
  };

  const canGoPrev = currentMonth.year > today.getFullYear() || currentMonth.month > today.getMonth();

  const submitBooking = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        meeting_type: meetingType,
        date: formatDate(selectedDate),
        time: selectedTime,
        message: form.message || null
      });
      setConfirmation(res.data);
      setStep(4);
      toast.success("Booking confirmed!");
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to book";
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const selectedDateStr = selectedDate
    ? `${DAYS[selectedDate.getDay()]}, ${MONTHS[selectedDate.getMonth()]} ${selectedDate.getDate()}, ${selectedDate.getFullYear()}`
    : "";

  return (
    <div data-testid="booking-page" className="pt-40 pb-16 min-h-screen bg-[#050505]">
      <SEO
        title="Book a Consultation"
        description="Book a free consultation with OBRA Creatives, photographer and videographer for Manawatū and Wellington. Zoom or phone call, Monday to Friday, 10am-6pm NZT."
        path="/book"
      />
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <span className="text-[#D4C5B0] text-xs uppercase tracking-[0.3em] mb-4 block">Book a Consultation</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#EDEDED] mb-4">Let's Talk</h1>
          <p className="text-[#A3A3A3] text-base max-w-lg mx-auto">
            Schedule a free consultation to discuss your project. Choose a time that works for you.
          </p>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-3 mb-12">
          {[
            { n: 1, label: "Date" },
            { n: 2, label: "Time" },
            { n: 3, label: "Details" },
            { n: 4, label: "Done" }
          ].map((s, i) => (
            <div key={s.n} className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                step >= s.n ? "bg-[#D4C5B0] text-[#050505]" : "bg-[#1A1A1A] text-[#555]"
              }`}>
                {step > s.n ? <Check size={14} /> : s.n}
              </div>
              <span className={`text-xs uppercase tracking-wider hidden sm:block ${step >= s.n ? "text-[#EDEDED]" : "text-[#555]"}`}>{s.label}</span>
              {i < 3 && <div className={`w-8 h-px ${step > s.n ? "bg-[#D4C5B0]" : "bg-[#262626]"}`} />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Calendar */}
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="max-w-md mx-auto">
              <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl p-6">
                {/* Month nav */}
                <div className="flex items-center justify-between mb-6">
                  <button onClick={prevMonth} disabled={!canGoPrev}
                    className="w-8 h-8 flex items-center justify-center text-[#A3A3A3] hover:text-[#EDEDED] disabled:opacity-30 disabled:cursor-not-allowed">
                    <ArrowLeft size={18} />
                  </button>
                  <h3 className="text-[#EDEDED] font-semibold">{MONTHS[currentMonth.month]} {currentMonth.year}</h3>
                  <button onClick={nextMonth}
                    className="w-8 h-8 flex items-center justify-center text-[#A3A3A3] hover:text-[#EDEDED]">
                    <ArrowRight size={18} />
                  </button>
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {DAYS.map(d => (
                    <div key={d} className="text-center text-[#555] text-xs py-1">{d}</div>
                  ))}
                </div>

                {/* Calendar days */}
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                    const weekday = isWeekday(day);
                    const past = isPast(day);
                    const available = weekday && !past;
                    const isToday = currentMonth.year === today.getFullYear() && currentMonth.month === today.getMonth() && day === today.getDate();
                    const isSelected = selectedDate && selectedDate.getDate() === day && selectedDate.getMonth() === currentMonth.month && selectedDate.getFullYear() === currentMonth.year;

                    return (
                      <button
                        key={day}
                        onClick={() => available && selectDate(day)}
                        disabled={!available}
                        data-testid={`cal-day-${day}`}
                        className={`aspect-square rounded-lg flex items-center justify-center text-sm transition-all ${
                          isSelected ? "bg-[#D4C5B0] text-[#050505] font-bold" :
                          available ? "text-[#EDEDED] hover:bg-[#D4C5B0]/20 cursor-pointer" :
                          "text-[#333] cursor-not-allowed"
                        } ${isToday && !isSelected ? "ring-1 ring-[#D4C5B0]/50" : ""}`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>

                <p className="text-[#555] text-xs mt-4 text-center">Mon – Fri, 10 AM – 6 PM</p>
              </div>
            </motion.div>
          )}

          {/* Step 2: Time + Meeting Type */}
          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="max-w-lg mx-auto">
              <button onClick={() => setStep(1)} className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#EDEDED] text-sm mb-6">
                <ArrowLeft size={16} /> Change date
              </button>

              <p className="text-[#D4C5B0] text-sm mb-6">{selectedDateStr}</p>

              {/* Meeting Type */}
              <div className="mb-8">
                <h3 className="text-[#EDEDED] font-semibold mb-3">Meeting Type</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: "zoom", label: "Zoom Meeting", icon: Video, desc: "Video call via Zoom" },
                    { id: "call", label: "Phone Call", icon: Phone, desc: "We'll call you" }
                  ].map(t => (
                    <button key={t.id} onClick={() => setMeetingType(t.id)} data-testid={`meeting-type-${t.id}`}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        meetingType === t.id
                          ? "border-[#D4C5B0] bg-[#D4C5B0]/10"
                          : "border-[#1A1A1A] bg-[#0E0E0E] hover:border-[#333]"
                      }`}>
                      <t.icon size={24} className={meetingType === t.id ? "text-[#D4C5B0] mb-2" : "text-[#555] mb-2"} />
                      <p className="text-[#EDEDED] font-medium text-sm">{t.label}</p>
                      <p className="text-[#555] text-xs mt-1">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Slots */}
              <div>
                <h3 className="text-[#EDEDED] font-semibold mb-3">Select Time</h3>
                {loadingSlots ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-[#D4C5B0] animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {slots.map(slot => (
                      <button
                        key={slot.time}
                        onClick={() => slot.available && setSelectedTime(slot.time)}
                        disabled={!slot.available}
                        data-testid={`slot-${slot.time}`}
                        className={`py-3 rounded-lg text-sm font-medium transition-all ${
                          selectedTime === slot.time
                            ? "bg-[#D4C5B0] text-[#050505]"
                            : slot.available
                              ? "bg-[#0E0E0E] border border-[#1A1A1A] text-[#EDEDED] hover:border-[#D4C5B0]"
                              : "bg-[#0A0A0A] text-[#333] cursor-not-allowed line-through"
                        }`}
                      >
                        {slot.display}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => setStep(3)}
                disabled={!selectedTime || !meetingType}
                data-testid="continue-to-details"
                className="w-full mt-8 btn-primary flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue <ArrowRight size={16} />
              </button>
            </motion.div>
          )}

          {/* Step 3: Contact Details */}
          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="max-w-lg mx-auto">
              <button onClick={() => setStep(2)} className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#EDEDED] text-sm mb-6">
                <ArrowLeft size={16} /> Back
              </button>

              {/* Summary */}
              <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl p-4 mb-8 flex items-center gap-4">
                <div className="w-12 h-12 bg-[#D4C5B0]/10 rounded-lg flex items-center justify-center">
                  {meetingType === "zoom" ? <Video size={20} className="text-[#D4C5B0]" /> : <Phone size={20} className="text-[#D4C5B0]" />}
                </div>
                <div>
                  <p className="text-[#EDEDED] font-medium text-sm">
                    {meetingType === "zoom" ? "Zoom Meeting" : "Phone Call"} · 1 Hour
                  </p>
                  <p className="text-[#A3A3A3] text-xs">
                    {selectedDateStr} · {slots.find(s => s.time === selectedTime)?.display}
                  </p>
                </div>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-[#555] text-xs mb-1.5">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
                    <input type="text" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="Your name" data-testid="booking-name"
                      className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] pl-10 pr-4 py-3 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-[#555] text-xs mb-1.5">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
                    <input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="your@email.com" data-testid="booking-email"
                      className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] pl-10 pr-4 py-3 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-[#555] text-xs mb-1.5">Phone {meetingType === "call" ? "*" : "(optional)"}</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
                    <input type="tel" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                      placeholder="+64 21 xxx xxxx" data-testid="booking-phone"
                      className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] pl-10 pr-4 py-3 rounded-lg text-sm focus:border-[#D4C5B0] focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-[#555] text-xs mb-1.5">Message (optional)</label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-[#555]" />
                    <textarea value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))}
                      placeholder="Tell us about your project..." rows={3} data-testid="booking-message"
                      className="w-full bg-[#1A1A1A] border border-[#262626] text-[#EDEDED] pl-10 pr-4 py-3 rounded-lg text-sm resize-none focus:border-[#D4C5B0] focus:outline-none" />
                  </div>
                </div>
              </div>

              <button
                onClick={submitBooking}
                disabled={submitting || !form.name || !form.email || (meetingType === "call" && !form.phone)}
                data-testid="confirm-booking"
                className="w-full mt-8 btn-primary flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? (<><Loader2 size={16} className="animate-spin" /> Booking...</>)
                  : (<><Check size={16} /> Confirm Booking</>)}
              </button>
            </motion.div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && confirmation && (
            <motion.div key="step4" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="max-w-md mx-auto text-center">
              <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-xl p-10">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check size={32} className="text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-[#EDEDED] mb-2">Booking Confirmed!</h2>
                <p className="text-[#A3A3A3] mb-8">We'll send you the details via email.</p>

                <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-6 text-left space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#555] text-sm">Type</span>
                    <span className="text-[#EDEDED] text-sm font-medium">{confirmation.meeting_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#555] text-sm">Date</span>
                    <span className="text-[#EDEDED] text-sm font-medium">{selectedDateStr}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#555] text-sm">Time</span>
                    <span className="text-[#EDEDED] text-sm font-medium">{confirmation.time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#555] text-sm">Duration</span>
                    <span className="text-[#EDEDED] text-sm font-medium">1 Hour</span>
                  </div>
                </div>

                <button
                  onClick={() => { setStep(1); setSelectedDate(null); setSelectedTime(null); setMeetingType(null); setForm({ name: "", email: "", phone: "", message: "" }); setConfirmation(null); }}
                  className="mt-8 text-[#D4C5B0] text-sm hover:underline"
                >
                  Book another consultation
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
